/**
 * Scheduling actions (classes, PT sessions, etc.).
 * GET /actions/scheduling/classes — list classes (proxies to api/v1/...).
 * POST /actions/scheduling/classes — create class.
 * GET /actions/scheduling/pt-sessions — list PT sessions for the current coach.
 * POST /actions/scheduling/pt-sessions — create PT session. JSON body: include `windows`
 *   as a real array of `{ day_of_week, start_time, end_time }` (multiple entries per day
 *   allowed for non-overlapping blocks). Not form-encoded `windows[0][…]`. Session cookie
 *   supplies auth; include `coach_id` in body when the API requires it.
 * GET /actions/scheduling/pt-sessions/:sessionId/availability-windows — windows + weekly_availability (proxies api/v1/scheduling/pt-sessions/:id/availability-windows).
 * Facilities (upstream API: `scheduling/facilities`):
 * GET /actions/scheduling/facilities — list (proxies GET `scheduling/facilities`).
 * POST /actions/scheduling/facilities — create (proxies POST `scheduling/facilities`, JSON body).
 * GET /actions/scheduling/facilities/:facilityId/availability-windows — weekly windows (proxies `scheduling/facilities/:id/availability-windows`).
 * PUT /actions/scheduling/classes/:classId — update class (proxies PUT `scheduling/classes/:id`).
 * DELETE /actions/scheduling/classes/:classId — delete class.
 * PUT /actions/scheduling/pt-sessions/:sessionId — update PT session.
 * DELETE /actions/scheduling/pt-sessions/:sessionId — delete PT session.
 * PUT /actions/scheduling/facilities/:facilityId — update facility.
 * DELETE /actions/scheduling/facilities/:facilityId — delete facility.
 * GET /actions/scheduling/blackouts — list coach blackouts (proxies GET `scheduling/blackouts`).
 * PATCH /actions/scheduling/blackouts/:blackoutId — update coach blackout (proxies PATCH
 *   `scheduling/blackouts/:id`).
 * DELETE /actions/scheduling/blackouts/:blackoutId — delete coach blackout (proxies DELETE
 *   `scheduling/blackouts/:id`).
 * GET /actions/scheduling/coach/bookings — list bookings for the current coach (proxies GET `scheduling/coach/bookings`).
 * POST /actions/scheduling/bookings/:bookingId/attendance — mark attendance (proxies POST `scheduling/bookings/:id/attendance`). JSON: { status: "attended" | "absent" }.
 * GET /actions/scheduling/overrides — list availability overrides (proxies GET `scheduling/overrides`).
 * POST /actions/scheduling/blackouts — create coach blackout (proxies POST
 *   `scheduling/blackouts` → upstream e.g. /api/scheduling/blackouts). JSON: action
 *   (coach_wide | event_type_batch | single_event), name, description, schedule_type,
 *   time_mode, confirm; `single_day: { date }`; `date_range: { start_date, end_date }`;
 *   recurring: `recurring_weekdays` (Python weekday: Mon=0 … Sun=6); either
 *   `recurring_ends: "never"` or
 *   `recurring_until_date` (YYYY-MM-DD).
 * POST/PUT scheduling/classes, pt-sessions, facilities with multipart/form-data
 *   (fields thumbnail, facility_image, images, …) proxies to the API for S3-backed URLs.
 *   Multipart forwards send metadata as one JSON `data` field (same shape as JSON POST)
 *   via `multipartJsonBody` in RillFetcher — not qFlat’d form fields.
 */
function parseJsonBody(req, res) {
  let body = req.body;
  if (typeof body === "string") {
    try {
      body = JSON.parse(body);
    } catch {
      res.status = 400;
      res.headers = res.headers || {};
      res.headers["Content-Type"] = "application/json; charset=utf-8";
      res.body = JSON.stringify({ error: "Invalid JSON body" });
      return null;
    }
  }
  return body || {};
}

/**
 * RillFetcher turns object `body` into multipart FormData via qFlat (keys like
 * `windows[0][day_of_week]`). Upstream /api/v1 expects real JSON with a `windows` array.
 */
function schedulingUpstreamJsonOpts(body) {
  return {
    body: JSON.stringify(body ?? {}),
    headers: { "Content-Type": "application/json" },
  };
}

/**
 * Rebuild the same object shape as `application/json` POST from multipart text fields
 * (or a single `data` JSON string from the browser). Used before forwarding upstream.
 */
function schedulingNormalizeMultipartBody(body) {
  if (!body || typeof body !== "object") return {};
  if (typeof body.data === "string" && body.data.trim().startsWith("{")) {
    try {
      const parsed = JSON.parse(body.data);
      if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) {
        return parsed;
      }
    } catch {
      /* fall through */
    }
  }
  const copy = { ...body };
  delete copy.data;
  const parseJsonMaybe = (val) => {
    if (typeof val !== "string") return val;
    const t = val.trim();
    if (!t.startsWith("[") && !t.startsWith("{")) return val;
    try {
      return JSON.parse(val);
    } catch {
      return val;
    }
  };
  for (const k of [
    "windows",
    "image_urls",
    "allowed_user_types",
    "allowed_membership_plans",
    "coach_ids",
  ]) {
    if (k in copy) copy[k] = parseJsonMaybe(copy[k]);
  }
  for (const k of ["is_restricted", "pull_only", "booking_limit_enabled"]) {
    if (copy[k] === "true") copy[k] = true;
    if (copy[k] === "false") copy[k] = false;
  }
  for (const k of [
    "duration_minutes",
    "capacity",
    "booking_limit_count",
    "booking_cutoff",
  ]) {
    if (
      typeof copy[k] === "string" &&
      copy[k] !== "" &&
      !Number.isNaN(Number(copy[k]))
    ) {
      copy[k] = Number(copy[k]);
    }
  }
  return copy;
}

function schedulingMultipartMetadataJson(reqBody) {
  return JSON.stringify(schedulingNormalizeMultipartBody(reqBody));
}

function schedulingReqHasUploadFiles(files) {
  if (!files || typeof files !== "object") return false;
  for (const key of Object.keys(files)) {
    const v = files[key];
    if (v == null) continue;
    if (Array.isArray(v)) {
      if (v.length > 0) return true;
    } else {
      return true;
    }
  }
  return false;
}

const MAX_BOOKING_PAGES = 100;

const Scheduling = (app) => {
  app.get("/scheduling/coaches", async ({ res, api }) => {
    res.headers = res.headers || {};
    res.headers["Content-Type"] = "application/json; charset=utf-8";
    try {
      const response = await api("scheduling/coaches");
      if (!response.ok) {
        res.status = response.status;
        res.body = await response.text();
        return;
      }
      const body = await response.json();
      const list = Array.isArray(body?.coaches) ? body.coaches : [];
      const trimmed = list
        .map((c) => ({
          _id: String(c?._id ?? c?.id ?? ""),
          name: String(c?.name ?? "").trim(),
          email: String(c?.email ?? "").trim(),
          avatar:
            c?.avatar && typeof c.avatar === "object" && c.avatar.s3Path
              ? { s3Path: String(c.avatar.s3Path) }
              : null,
          profile_image_url:
            typeof c?.profile_image_url === "string"
              ? c.profile_image_url
              : typeof c?.avatar === "string"
                ? c.avatar
                : "",
        }))
        .filter((c) => c._id && c.name);
      res.status = 200;
      res.body = JSON.stringify({ coaches: trimmed });
    } catch (e) {
      res.status = 500;
      res.body = JSON.stringify({
        error: e?.message || "Failed to load coaches",
      });
    }
  });

  app.get("/scheduling/classes", async ({ req, res, api, locals }) => {
    res.headers = res.headers || {};
    res.headers["Content-Type"] = "application/json; charset=utf-8";
    try {
      const coachId = locals?.activeUser?._id;
      const opts = { query: coachId ? { coachId } : {} };
      const response = await api("scheduling/classes", opts);
      if (!response.ok) {
        res.status = response.status === 404 ? 200 : response.status;
        res.body = response.status === 404 ? "[]" : await response.text();
        return;
      }
      const data = await response.json().catch(() => ({}));
      const list = Array.isArray(data)
        ? data
        : Array.isArray(data?.data)
          ? data.data
          : Array.isArray(data?.classes)
            ? data.classes
            : Array.isArray(data?.items)
              ? data.items
              : Array.isArray(data?.results)
                ? data.results
                : [];
      res.status = 200;
      res.body = JSON.stringify(list);
    } catch (e) {
      res.status = 500;
      res.body = JSON.stringify({
        error: e.message || "Failed to load classes",
      });
    }
  });

  app.post("/scheduling/classes", async ({ req, res, api }) => {
    res.headers = res.headers || {};
    res.headers["Content-Type"] = "application/json; charset=utf-8";
    if (schedulingReqHasUploadFiles(req.files)) {
      try {
        const response = await api("scheduling/classes", {
          method: "POST",
          multipartJsonBody: schedulingMultipartMetadataJson(req.body),
          files: req.files,
        });
        if (!response.ok) {
          const text = await response.text();
          let errorPayload = { error: "Failed to create class" };
          try {
            errorPayload = JSON.parse(text);
          } catch {
            if (text) errorPayload.error = text;
          }
          res.status = response.status;
          res.body = JSON.stringify(errorPayload);
          return;
        }
        const created = await response.json().catch(() => ({}));
        res.status = 201;
        res.body = JSON.stringify(created);
      } catch (e) {
        res.status = 500;
        res.body = JSON.stringify({
          error: e.message || "Failed to create class",
        });
      }
      return;
    }
    let body = req.body;
    if (typeof body === "string") {
      try {
        body = JSON.parse(body);
      } catch {
        res.status = 400;
        res.body = JSON.stringify({ error: "Invalid JSON body" });
        return;
      }
    }
    body = body || {};
    try {
      const response = await api("scheduling/classes", {
        method: "POST",
        ...schedulingUpstreamJsonOpts(body),
      });
      if (!response.ok) {
        const text = await response.text();
        let errorPayload = { error: "Failed to create class" };
        try {
          errorPayload = JSON.parse(text);
        } catch {
          if (text) errorPayload.error = text;
        }
        res.status = response.status;
        res.headers = res.headers || {};
        res.headers["Content-Type"] = "application/json; charset=utf-8";
        res.body = JSON.stringify(errorPayload);
        return;
      }
      const created = await response.json().catch(() => ({}));
      res.status = 201;
      res.body = JSON.stringify(created);
    } catch (e) {
      res.status = 500;
      res.body = JSON.stringify({
        error: e.message || "Failed to create class",
      });
    }
  });

  app.put("/scheduling/classes/:classId", async ({ req, res, api }) => {
    const { classId } = req.params || {};
    res.headers = res.headers || {};
    res.headers["Content-Type"] = "application/json; charset=utf-8";
    if (!classId) {
      res.status = 400;
      res.body = JSON.stringify({ error: "Missing class id" });
      return;
    }
    if (schedulingReqHasUploadFiles(req.files)) {
      try {
        const path = `scheduling/classes/${encodeURIComponent(classId)}`;
        const response = await api(path, {
          method: "PUT",
          multipartJsonBody: schedulingMultipartMetadataJson(req.body),
          files: req.files,
        });
        const text = await response.text();
        if (!response.ok) {
          let errorPayload = { error: "Failed to update class" };
          try {
            errorPayload = text ? JSON.parse(text) : errorPayload;
          } catch {
            if (text) errorPayload.error = text;
          }
          res.status = response.status;
          res.body = JSON.stringify(errorPayload);
          return;
        }
        res.status = 200;
        res.body = text || "{}";
      } catch (e) {
        res.status = 500;
        res.body = JSON.stringify({
          error: e.message || "Failed to update class",
        });
      }
      return;
    }
    const body = parseJsonBody(req, res);
    if (body == null) return;
    try {
      const path = `scheduling/classes/${encodeURIComponent(classId)}`;
      const response = await api(path, {
        method: "PUT",
        ...schedulingUpstreamJsonOpts(body),
      });
      const text = await response.text();
      if (!response.ok) {
        let errorPayload = { error: "Failed to update class" };
        try {
          errorPayload = text ? JSON.parse(text) : errorPayload;
        } catch {
          if (text) errorPayload.error = text;
        }
        res.status = response.status;
        res.body = JSON.stringify(errorPayload);
        return;
      }
      res.status = 200;
      res.body = text || "{}";
    } catch (e) {
      res.status = 500;
      res.body = JSON.stringify({
        error: e.message || "Failed to update class",
      });
    }
  });

  app.delete("/scheduling/classes/:classId", async ({ req, res, api }) => {
    const { classId } = req.params || {};
    res.headers = res.headers || {};
    res.headers["Content-Type"] = "application/json; charset=utf-8";
    if (!classId) {
      res.status = 400;
      res.body = JSON.stringify({ error: "Missing class id" });
      return;
    }
    try {
      const path = `scheduling/classes/${encodeURIComponent(classId)}`;
      const response = await api(path, { method: "DELETE" });
      const text = await response.text();
      if (!response.ok) {
        let errorPayload = { error: "Failed to delete class" };
        try {
          errorPayload = text ? JSON.parse(text) : errorPayload;
        } catch {
          if (text) errorPayload.error = text;
        }
        res.status = response.status;
        res.body = JSON.stringify(errorPayload);
        return;
      }
      res.status = response.status === 204 ? 204 : 200;
      res.body = text || "{}";
    } catch (e) {
      res.status = 500;
      res.body = JSON.stringify({
        error: e.message || "Failed to delete class",
      });
    }
  });

  app.get(
    "/scheduling/pt-sessions/:sessionId/availability-windows",
    async ({ res, api, req }) => {
      const { sessionId } = req.params || {};
      res.headers = res.headers || {};
      res.headers["Content-Type"] = "application/json; charset=utf-8";
      if (!sessionId) {
        res.status = 400;
        res.body = JSON.stringify({ error: "Missing session id" });
        return;
      }
      try {
        const path = `scheduling/pt-sessions/${encodeURIComponent(sessionId)}/availability-windows`;
        const response = await api(path);
        if (!response.ok) {
          const text = await response.text();
          res.status = response.status;
          res.body =
            text ||
            JSON.stringify({
              error: "Failed to load availability windows",
            });
          return;
        }
        const data = await response.json().catch(() => ({}));
        res.status = 200;
        res.body = JSON.stringify(data);
      } catch (e) {
        res.status = 500;
        res.body = JSON.stringify({
          error: e.message || "Failed to load availability windows",
        });
      }
    }
  );

  app.get("/scheduling/pt-sessions", async ({ res, api, locals }) => {
    res.headers = res.headers || {};
    res.headers["Content-Type"] = "application/json; charset=utf-8";
    try {
      const coachId = locals?.activeUser?._id;
      const opts = coachId ? { query: { coachId } } : {};
      const response = await api("scheduling/pt-sessions", opts);
      if (!response.ok) {
        res.status = response.status === 404 ? 200 : response.status;
        res.body = response.status === 404 ? "[]" : await response.text();
        return;
      }
      const data = await response.json().catch(() => ({}));
      const list = Array.isArray(data)
        ? data
        : Array.isArray(data?.data)
          ? data.data
          : Array.isArray(data?.pt_sessions)
            ? data.pt_sessions
            : Array.isArray(data?.ptSessions)
              ? data.ptSessions
              : Array.isArray(data?.sessions)
                ? data.sessions
                : Array.isArray(data?.items)
                  ? data.items
                  : Array.isArray(data?.results)
                    ? data.results
                    : [];
      res.status = 200;
      res.body = JSON.stringify(list);
    } catch (e) {
      res.status = 500;
      res.body = JSON.stringify({
        error: e.message || "Failed to load PT sessions",
      });
    }
  });

  app.post("/scheduling/pt-sessions", async ({ req, res, api }) => {
    res.headers = res.headers || {};
    res.headers["Content-Type"] = "application/json; charset=utf-8";
    if (schedulingReqHasUploadFiles(req.files)) {
      try {
        const response = await api("scheduling/pt-sessions", {
          method: "POST",
          multipartJsonBody: schedulingMultipartMetadataJson(req.body),
          files: req.files,
        });
        if (!response.ok) {
          const text = await response.text();
          let errorPayload = { error: "Failed to create PT session" };
          try {
            errorPayload = JSON.parse(text);
          } catch {
            if (text) errorPayload.error = text;
          }
          res.status = response.status;
          res.body = JSON.stringify(errorPayload);
          return;
        }
        const created = await response.json().catch(() => ({}));
        res.status = 201;
        res.body = JSON.stringify(created);
      } catch (e) {
        res.status = 500;
        res.body = JSON.stringify({
          error: e.message || "Failed to create PT session",
        });
      }
      return;
    }
    let body = req.body;
    if (typeof body === "string") {
      try {
        body = JSON.parse(body);
      } catch {
        res.status = 400;
        res.body = JSON.stringify({ error: "Invalid JSON body" });
        return;
      }
    }
    body = body || {};
    try {
      const response = await api("scheduling/pt-sessions", {
        method: "POST",
        ...schedulingUpstreamJsonOpts(body),
      });
      if (!response.ok) {
        const text = await response.text();
        let errorPayload = { error: "Failed to create PT session" };
        try {
          errorPayload = JSON.parse(text);
        } catch {
          if (text) errorPayload.error = text;
        }
        res.status = response.status;
        res.headers = res.headers || {};
        res.headers["Content-Type"] = "application/json; charset=utf-8";
        res.body = JSON.stringify(errorPayload);
        return;
      }
      const created = await response.json().catch(() => ({}));
      res.status = 201;
      res.body = JSON.stringify(created);
    } catch (e) {
      res.status = 500;
      res.body = JSON.stringify({
        error: e.message || "Failed to create PT session",
      });
    }
  });

  app.put("/scheduling/pt-sessions/:sessionId", async ({ req, res, api }) => {
    const { sessionId } = req.params || {};
    res.headers = res.headers || {};
    res.headers["Content-Type"] = "application/json; charset=utf-8";
    if (!sessionId) {
      res.status = 400;
      res.body = JSON.stringify({ error: "Missing session id" });
      return;
    }
    if (schedulingReqHasUploadFiles(req.files)) {
      try {
        const path = `scheduling/pt-sessions/${encodeURIComponent(sessionId)}`;
        const response = await api(path, {
          method: "PUT",
          multipartJsonBody: schedulingMultipartMetadataJson(req.body),
          files: req.files,
        });
        const text = await response.text();
        if (!response.ok) {
          let errorPayload = { error: "Failed to update PT session" };
          try {
            errorPayload = text ? JSON.parse(text) : errorPayload;
          } catch {
            if (text) errorPayload.error = text;
          }
          res.status = response.status;
          res.body = JSON.stringify(errorPayload);
          return;
        }
        res.status = 200;
        res.body = text || "{}";
      } catch (e) {
        res.status = 500;
        res.body = JSON.stringify({
          error: e.message || "Failed to update PT session",
        });
      }
      return;
    }
    const body = parseJsonBody(req, res);
    if (body == null) return;
    try {
      const path = `scheduling/pt-sessions/${encodeURIComponent(sessionId)}`;
      const response = await api(path, {
        method: "PUT",
        ...schedulingUpstreamJsonOpts(body),
      });
      const text = await response.text();
      if (!response.ok) {
        let errorPayload = { error: "Failed to update PT session" };
        try {
          errorPayload = text ? JSON.parse(text) : errorPayload;
        } catch {
          if (text) errorPayload.error = text;
        }
        res.status = response.status;
        res.body = JSON.stringify(errorPayload);
        return;
      }
      res.status = 200;
      res.body = text || "{}";
    } catch (e) {
      res.status = 500;
      res.body = JSON.stringify({
        error: e.message || "Failed to update PT session",
      });
    }
  });

  app.delete(
    "/scheduling/pt-sessions/:sessionId",
    async ({ req, res, api }) => {
      const { sessionId } = req.params || {};
      res.headers = res.headers || {};
      res.headers["Content-Type"] = "application/json; charset=utf-8";
      if (!sessionId) {
        res.status = 400;
        res.body = JSON.stringify({ error: "Missing session id" });
        return;
      }
      try {
        const path = `scheduling/pt-sessions/${encodeURIComponent(sessionId)}`;
        const response = await api(path, { method: "DELETE" });
        const text = await response.text();
        if (!response.ok) {
          let errorPayload = { error: "Failed to delete PT session" };
          try {
            errorPayload = text ? JSON.parse(text) : errorPayload;
          } catch {
            if (text) errorPayload.error = text;
          }
          res.status = response.status;
          res.body = JSON.stringify(errorPayload);
          return;
        }
        res.status = response.status === 204 ? 204 : 200;
        res.body = text || "{}";
      } catch (e) {
        res.status = 500;
        res.body = JSON.stringify({
          error: e.message || "Failed to delete PT session",
        });
      }
    }
  );

  app.get(
    "/scheduling/facilities/:facilityId/availability-windows",
    async ({ res, api, req }) => {
      const { facilityId } = req.params || {};
      res.headers = res.headers || {};
      res.headers["Content-Type"] = "application/json; charset=utf-8";
      if (!facilityId) {
        res.status = 400;
        res.body = JSON.stringify({ error: "Missing facility id" });
        return;
      }
      try {
        const path = `scheduling/facilities/${encodeURIComponent(facilityId)}/availability-windows`;
        const response = await api(path);
        if (!response.ok) {
          const text = await response.text();
          res.status = response.status;
          res.body =
            text ||
            JSON.stringify({
              error: "Failed to load availability windows",
            });
          return;
        }
        const data = await response.json().catch(() => ({}));
        res.status = 200;
        res.body = JSON.stringify(data);
      } catch (e) {
        res.status = 500;
        res.body = JSON.stringify({
          error: e.message || "Failed to load availability windows",
        });
      }
    }
  );

  app.get("/scheduling/facilities", async ({ res, api, locals }) => {
    res.headers = res.headers || {};
    res.headers["Content-Type"] = "application/json; charset=utf-8";
    try {
      const coachId = locals?.activeUser?._id;
      const opts = coachId ? { query: { coachId } } : {};
      const response = await api("scheduling/facilities", opts);
      if (!response.ok) {
        res.status = response.status === 404 ? 200 : response.status;
        res.body = response.status === 404 ? "[]" : await response.text();
        return;
      }
      const data = await response.json().catch(() => ({}));
      const list = Array.isArray(data)
        ? data
        : Array.isArray(data?.data)
          ? data.data
          : Array.isArray(data?.facilities)
            ? data.facilities
            : Array.isArray(data?.items)
              ? data.items
              : Array.isArray(data?.results)
                ? data.results
                : [];
      res.status = 200;
      res.body = JSON.stringify(list);
    } catch (e) {
      res.status = 500;
      res.body = JSON.stringify({
        error: e.message || "Failed to load facilities",
      });
    }
  });

  app.post("/scheduling/facilities", async ({ req, res, api }) => {
    res.headers = res.headers || {};
    res.headers["Content-Type"] = "application/json; charset=utf-8";
    if (schedulingReqHasUploadFiles(req.files)) {
      try {
        const response = await api("scheduling/facilities", {
          method: "POST",
          multipartJsonBody: schedulingMultipartMetadataJson(req.body),
          files: req.files,
        });
        if (!response.ok) {
          const text = await response.text();
          let errorPayload = { error: "Failed to create facility" };
          try {
            errorPayload = JSON.parse(text);
          } catch {
            if (text) errorPayload.error = text;
          }
          res.status = response.status;
          res.body = JSON.stringify(errorPayload);
          return;
        }
        const created = await response.json().catch(() => ({}));
        res.status = 201;
        res.body = JSON.stringify(created);
      } catch (e) {
        res.status = 500;
        res.body = JSON.stringify({
          error: e.message || "Failed to create facility",
        });
      }
      return;
    }
    let body = req.body;
    if (typeof body === "string") {
      try {
        body = JSON.parse(body);
      } catch {
        res.status = 400;
        res.body = JSON.stringify({ error: "Invalid JSON body" });
        return;
      }
    }
    body = body || {};
    try {
      const response = await api("scheduling/facilities", {
        method: "POST",
        ...schedulingUpstreamJsonOpts(body),
      });
      if (!response.ok) {
        const text = await response.text();
        let errorPayload = { error: "Failed to create facility" };
        try {
          errorPayload = JSON.parse(text);
        } catch {
          if (text) errorPayload.error = text;
        }
        res.status = response.status;
        res.headers = res.headers || {};
        res.headers["Content-Type"] = "application/json; charset=utf-8";
        res.body = JSON.stringify(errorPayload);
        return;
      }
      const created = await response.json().catch(() => ({}));
      res.status = 201;
      res.body = JSON.stringify(created);
    } catch (e) {
      res.status = 500;
      res.body = JSON.stringify({
        error: e.message || "Failed to create facility",
      });
    }
  });

  app.put("/scheduling/facilities/:facilityId", async ({ req, res, api }) => {
    const { facilityId } = req.params || {};
    res.headers = res.headers || {};
    res.headers["Content-Type"] = "application/json; charset=utf-8";
    if (!facilityId) {
      res.status = 400;
      res.body = JSON.stringify({ error: "Missing facility id" });
      return;
    }
    if (schedulingReqHasUploadFiles(req.files)) {
      try {
        const path = `scheduling/facilities/${encodeURIComponent(facilityId)}`;
        const response = await api(path, {
          method: "PUT",
          multipartJsonBody: schedulingMultipartMetadataJson(req.body),
          files: req.files,
        });
        const text = await response.text();
        if (!response.ok) {
          let errorPayload = { error: "Failed to update facility" };
          try {
            errorPayload = text ? JSON.parse(text) : errorPayload;
          } catch {
            if (text) errorPayload.error = text;
          }
          res.status = response.status;
          res.body = JSON.stringify(errorPayload);
          return;
        }
        res.status = 200;
        res.body = text || "{}";
      } catch (e) {
        res.status = 500;
        res.body = JSON.stringify({
          error: e.message || "Failed to update facility",
        });
      }
      return;
    }
    const body = parseJsonBody(req, res);
    if (body == null) return;
    try {
      const path = `scheduling/facilities/${encodeURIComponent(facilityId)}`;
      const response = await api(path, {
        method: "PUT",
        ...schedulingUpstreamJsonOpts(body),
      });
      const text = await response.text();
      if (!response.ok) {
        let errorPayload = { error: "Failed to update facility" };
        try {
          errorPayload = text ? JSON.parse(text) : errorPayload;
        } catch {
          if (text) errorPayload.error = text;
        }
        res.status = response.status;
        res.body = JSON.stringify(errorPayload);
        return;
      }
      res.status = 200;
      res.body = text || "{}";
    } catch (e) {
      res.status = 500;
      res.body = JSON.stringify({
        error: e.message || "Failed to update facility",
      });
    }
  });

  app.delete(
    "/scheduling/facilities/:facilityId",
    async ({ req, res, api }) => {
      const { facilityId } = req.params || {};
      res.headers = res.headers || {};
      res.headers["Content-Type"] = "application/json; charset=utf-8";
      if (!facilityId) {
        res.status = 400;
        res.body = JSON.stringify({ error: "Missing facility id" });
        return;
      }
      try {
        const path = `scheduling/facilities/${encodeURIComponent(facilityId)}`;
        const response = await api(path, { method: "DELETE" });
        const text = await response.text();
        if (!response.ok) {
          let errorPayload = { error: "Failed to delete facility" };
          try {
            errorPayload = text ? JSON.parse(text) : errorPayload;
          } catch {
            if (text) errorPayload.error = text;
          }
          res.status = response.status;
          res.body = JSON.stringify(errorPayload);
          return;
        }
        res.status = response.status === 204 ? 204 : 200;
        res.body = text || "{}";
      } catch (e) {
        res.status = 500;
        res.body = JSON.stringify({
          error: e.message || "Failed to delete facility",
        });
      }
    }
  );

  app.get("/scheduling/overrides", async ({ res, api, locals }) => {
    res.headers = res.headers || {};
    res.headers["Content-Type"] = "application/json; charset=utf-8";
    try {
      const coachId = locals?.activeUser?._id;
      const opts = coachId ? { query: { coachId } } : {};
      const response = await api("scheduling/overrides", opts);
      if (!response.ok) {
        res.status = response.status === 404 ? 200 : response.status;
        res.body = response.status === 404 ? "[]" : await response.text();
        return;
      }
      const data = await response.json().catch(() => ({}));
      const list = Array.isArray(data)
        ? data
        : Array.isArray(data?.data)
          ? data.data
          : Array.isArray(data?.overrides)
            ? data.overrides
            : Array.isArray(data?.items)
              ? data.items
              : Array.isArray(data?.results)
                ? data.results
                : [];
      res.status = 200;
      res.body = JSON.stringify(list);
    } catch (e) {
      res.status = 500;
      res.body = JSON.stringify({
        error: e.message || "Failed to load overrides",
      });
    }
  });

  app.get("/scheduling/blackouts", async ({ req, res, api, locals }) => {
    res.headers = res.headers || {};
    res.headers["Content-Type"] = "application/json; charset=utf-8";
    try {
      const coachId = locals?.activeUser?._id;
      const query = {};
      if (coachId) query.coachId = coachId;
      if (req.query?.scope === "org") query.scope = "org";
      const opts = Object.keys(query).length > 0 ? { query } : {};
      const response = await api("scheduling/blackouts", opts);
      if (!response.ok) {
        res.status = response.status === 404 ? 200 : response.status;
        res.body = response.status === 404 ? "[]" : await response.text();
        return;
      }
      const data = await response.json().catch(() => ({}));
      const list = Array.isArray(data)
        ? data
        : Array.isArray(data?.data)
          ? data.data
          : Array.isArray(data?.blackouts)
            ? data.blackouts
            : Array.isArray(data?.items)
              ? data.items
              : Array.isArray(data?.results)
                ? data.results
                : [];
      res.status = 200;
      res.body = JSON.stringify(list);
    } catch (e) {
      res.status = 500;
      res.body = JSON.stringify({
        error: e.message || "Failed to load blackouts",
      });
    }
  });

  app.post("/scheduling/blackouts", async ({ req, res, api }) => {
    res.headers = res.headers || {};
    res.headers["Content-Type"] = "application/json; charset=utf-8";
    const body = parseJsonBody(req, res);
    if (body == null) return;
    try {
      const response = await api("scheduling/blackouts", {
        method: "POST",
        ...schedulingUpstreamJsonOpts(body),
      });
      const text = await response.text();
      if (!response.ok) {
        let errorPayload = { error: "Failed to create blackout" };
        try {
          errorPayload = text ? JSON.parse(text) : errorPayload;
        } catch {
          if (text) errorPayload.error = text;
        }
        res.status = response.status;
        res.body = JSON.stringify(errorPayload);
        return;
      }
      res.status = response.status === 201 ? 201 : 200;
      res.body = text || "{}";
    } catch (e) {
      res.status = 500;
      res.body = JSON.stringify({
        error: e.message || "Failed to create blackout",
      });
    }
  });

  app.patch("/scheduling/blackouts/:blackoutId", async ({ req, res, api }) => {
    const { blackoutId } = req.params || {};
    res.headers = res.headers || {};
    res.headers["Content-Type"] = "application/json; charset=utf-8";
    if (!blackoutId) {
      res.status = 400;
      res.body = JSON.stringify({ error: "Missing blackout id" });
      return;
    }
    const body = parseJsonBody(req, res);
    if (body == null) return;
    try {
      const path = `scheduling/blackouts/${encodeURIComponent(blackoutId)}`;
      const response = await api(path, {
        method: "PATCH",
        ...schedulingUpstreamJsonOpts(body),
      });
      const text = await response.text();
      if (!response.ok) {
        let errorPayload = { error: "Failed to update blackout" };
        try {
          errorPayload = text ? JSON.parse(text) : errorPayload;
        } catch {
          if (text) errorPayload.error = text;
        }
        res.status = response.status;
        res.body = JSON.stringify(errorPayload);
        return;
      }
      res.status = response.status === 204 ? 204 : 200;
      res.body = text || "{}";
    } catch (e) {
      res.status = 500;
      res.body = JSON.stringify({
        error: e.message || "Failed to update blackout",
      });
    }
  });

  app.get("/scheduling/coach/bookings", async ({ req, res, api, locals }) => {
    res.headers = res.headers || {};
    res.headers["Content-Type"] = "application/json; charset=utf-8";
    try {
      const coachId = locals?.activeUser?._id;
      const filterEventId = req.query?.bookable_event_id ?? null;
      const filterDate = req.query?.date ?? null;
      const filterFrom = req.query?.from ?? null;
      const filterTo = req.query?.to ?? null;
      const includeMemberType = String(req.query?.include ?? "")
        .split(",")
        .includes("member_type");

      const buildQuery = (withCoach) => {
        const q = {};
        if (withCoach && coachId) q.coachId = coachId;
        if (filterEventId) q.bookable_event_id = filterEventId;
        if (filterDate) q.date = filterDate;
        if (filterFrom) q.from = filterFrom;
        if (filterTo) q.to = filterTo;
        return q;
      };

      const extractList = (data) => {
        if (Array.isArray(data)) return data;
        if (Array.isArray(data?.data)) return data.data;
        if (Array.isArray(data?.bookings)) return data.bookings;
        if (Array.isArray(data?.items)) return data.items;
        if (Array.isArray(data?.results)) return data.results;
        return [];
      };

      const perPage = filterDate || filterFrom || filterTo ? 1000 : 100;
      const fetchAllPages = async (q) => {
        const rows = [];
        for (let page = 1; page <= MAX_BOOKING_PAGES; page++) {
          const response = await api("scheduling/coach/bookings", {
            query: { ...q, page, per_page: perPage },
          });
          if (!response.ok) return { failed: response };
          const data = await response.json().catch(() => ({}));
          const pageRows = extractList(data);
          rows.push(...pageRows);
          // Stop on an empty page, not a short one: the API drops hidden
          // facility bookings after paging, so a short page may not be the last.
          if (pageRows.length === 0 || !Number(data?.per_page)) break;
        }
        return { rows };
      };

      // First try with coachId; if upstream returns nothing, retry without it
      // (handles APIs that auth via session token and ignore/reject the coachId param).
      let list = [];
      for (const withCoach of [true, false]) {
        const { failed, rows } = await fetchAllPages(buildQuery(withCoach));
        if (failed) {
          if (failed.status !== 404) {
            res.status = failed.status;
            res.body = await failed.text();
            return;
          }
          continue;
        }
        list = rows;
        if (list.length > 0) break;
      }

      const inferType = (b) => {
        if (b.classInstanceId) return "class";
        if (b.ptSessionInstanceId) return "pt_session";
        if (b.facilityInstanceId) return "facility";
        return null;
      };

      const typeToPath = {
        class: "scheduling/classes",
        pt_session: "scheduling/pt-sessions",
        facility: "scheduling/facilities",
      };

      const bookableRefId = (b) => {
        const ref = b.bookableEventId;
        if (ref && typeof ref === "object") return String(ref._id ?? "");
        return String(ref ?? "");
      };
      const bookablePopulated = (b) => {
        const ref = b.bookableEventId;
        return ref && typeof ref === "object" ? ref : null;
      };

      const memberIds = [
        ...new Set(
          list
            .filter((b) => includeMemberType || !b.member)
            .map((b) => b.memberId)
            .filter(Boolean)
            .map(String)
        ),
      ];

      // Separate bookableEventIds into known-type and unknown-type
      const knownEventEntries = new Map();
      const unknownEventIds = new Set();
      for (const b of list) {
        if (b.event) continue;
        const id = bookableRefId(b);
        if (!id || id === "undefined") continue;
        const type = inferType(b);
        if (type) {
          knownEventEntries.set(id, type);
        } else {
          unknownEventIds.add(id);
        }
      }

      const memberMap = {};
      const eventMap = {};

      // For unknown-type events, probe all three endpoints in parallel and use whichever hits
      const probeUnknown = async (id) => {
        const results = await Promise.all(
          Object.entries(typeToPath).map(async ([type, path]) => {
            try {
              const r = await api(`${path}/${encodeURIComponent(id)}`);
              if (r.ok) {
                const ev = await r.json().catch(() => null);
                if (ev) return { ev, type };
              }
            } catch {
              /* ignore */
            }
            return null;
          })
        );
        const hit = results.find(Boolean);
        if (hit) eventMap[id] = { ...hit.ev, _type: hit.type };
      };

      await Promise.all([
        ...memberIds.map(async (id) => {
          try {
            const r = await api(`user/${encodeURIComponent(id)}`);
            if (r.ok) {
              const u = await r.json().catch(() => null);
              if (u) memberMap[id] = u;
            }
          } catch {
            /* ignore */
          }
        }),
        ...[...knownEventEntries.entries()].map(async ([id, type]) => {
          try {
            const r = await api(
              `${typeToPath[type]}/${encodeURIComponent(id)}`
            );
            if (r.ok) {
              const ev = await r.json().catch(() => null);
              if (ev) eventMap[id] = { ...ev, _type: type };
            }
          } catch {
            /* ignore */
          }
        }),
        ...[...unknownEventIds].map(probeUnknown),
      ]);

      const enriched = list.map((b) => {
        const member = memberMap[String(b.memberId)] ?? b.member ?? null;
        const populated = bookablePopulated(b);
        const event = eventMap[bookableRefId(b)] ?? b.event ?? null;
        const tz = populated?.timezone ?? event?.timezone;
        return {
          ...b,
          member_name: member?.name ?? null,
          member_avatar: member?.avatar ?? null,
          member_email: member?.email ?? null,
          member_is_push: includeMemberType
            ? !!member?.pushAccountInfo
            : undefined,
          event_name: event?.name ?? populated?.name ?? null,
          event_type:
            inferType(b) ??
            event?._type ??
            event?.type ??
            populated?.type ??
            null,
          event_timezone: typeof tz === "string" && tz.length > 0 ? tz : null,
        };
      });

      // Client-side filter as fallback if upstream ignored bookable_event_id
      const finalList = filterEventId
        ? enriched.filter((b) => bookableRefId(b) === String(filterEventId))
        : enriched;

      res.status = 200;
      res.body = JSON.stringify(finalList);
    } catch (e) {
      res.status = 500;
      res.body = JSON.stringify({
        error: e.message || "Failed to load bookings",
      });
    }
  });

  app.post(
    "/scheduling/bookings/:bookingId/attendance",
    async ({ req, res, api }) => {
      const { bookingId } = req.params || {};
      res.headers = res.headers || {};
      res.headers["Content-Type"] = "application/json; charset=utf-8";
      if (!bookingId) {
        res.status = 400;
        res.body = JSON.stringify({ error: "Missing booking id" });
        return;
      }
      const body = parseJsonBody(req, res);
      if (body == null) return;
      try {
        const path = `scheduling/bookings/${encodeURIComponent(bookingId)}/attendance`;
        const response = await api(path, {
          method: "POST",
          ...schedulingUpstreamJsonOpts(body),
        });
        const text = await response.text();
        if (!response.ok) {
          let errorPayload = { error: "Failed to mark attendance" };
          try {
            errorPayload = text ? JSON.parse(text) : errorPayload;
          } catch {
            if (text) errorPayload.error = text;
          }
          res.status = response.status;
          res.body = JSON.stringify(errorPayload);
          return;
        }
        res.status = response.status === 201 ? 201 : 200;
        res.body = text || "{}";
      } catch (e) {
        res.status = 500;
        res.body = JSON.stringify({
          error: e.message || "Failed to mark attendance",
        });
      }
    }
  );

  app.delete("/scheduling/blackouts/:blackoutId", async ({ req, res, api }) => {
    const { blackoutId } = req.params || {};
    res.headers = res.headers || {};
    res.headers["Content-Type"] = "application/json; charset=utf-8";
    if (!blackoutId) {
      res.status = 400;
      res.body = JSON.stringify({ error: "Missing blackout id" });
      return;
    }
    try {
      const path = `scheduling/blackouts/${encodeURIComponent(blackoutId)}`;
      const response = await api(path, { method: "DELETE" });
      const text = await response.text();
      if (!response.ok) {
        let errorPayload = { error: "Failed to delete blackout" };
        try {
          errorPayload = text ? JSON.parse(text) : errorPayload;
        } catch {
          if (text) errorPayload.error = text;
        }
        res.status = response.status;
        res.body = JSON.stringify(errorPayload);
        return;
      }
      res.status = response.status === 204 ? 204 : 200;
      res.body = text || "{}";
    } catch (e) {
      res.status = 500;
      res.body = JSON.stringify({
        error: e.message || "Failed to delete blackout",
      });
    }
  });
};

export default Scheduling;
